import { useCallback, useContext } from 'react';
import { type BrandInsightsPageContext, type ChatContextItem, ChatContextType } from '@trinity/chat';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  CustomPageChatContextIdentifier,
  type CustomPageAction,
  type CustomPageActionSubject,
  type CustomPageDispatchAction,
} from '@trinity/custom-pages-registry';
import { ChatStateDispatchContext } from '../../Chat.State/context';
import type { ChatState } from '../../Chat.State/interfaces';
import { ActiveChatSessionContext, ChatContextItemsContext } from '../context';

const EMPTY_BATCH_ACTION: ChatState.BatchAction = {
  type: 'batch-actions',
  payload: {
    actions: [],
  },
};

function isCustomPageActionSubject(value: unknown): value is CustomPageActionSubject {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const subject = value as Record<string, unknown>;
  const hasValidId = typeof subject.id === 'string' || typeof subject.id === 'number';
  const hasValidName = typeof subject.name === 'string';

  if (!hasValidId || !hasValidName) {
    return false;
  }

  if (subject.link !== undefined && typeof subject.link !== 'string') {
    return false;
  }

  if (subject.kind !== undefined && typeof subject.kind !== 'string') {
    return false;
  }

  return true;
}

function hasSubjectPayload(payload: object | undefined): payload is { subject: CustomPageActionSubject } {
  if (!payload) {
    return false;
  }

  const payloadObject = payload as Record<string, unknown>;
  return isCustomPageActionSubject(payloadObject.subject);
}

function hasOpenNewChatSubjectPayload(payload: object | undefined): payload is {
  subject: CustomPageActionSubject;
  pendingQuery?: string | null;
  chatContextIdentifier: CustomPageChatContextIdentifier;
} {
  if (!hasSubjectPayload(payload)) {
    return false;
  }

  const payloadObject = payload as Record<string, unknown>;
  const pendingQuery = payloadObject.pendingQuery;
  const chatContextIdentifier = payloadObject.chatContextIdentifier;

  if (pendingQuery != null && typeof pendingQuery !== 'string') {
    return false;
  }

  return (
    typeof chatContextIdentifier === 'string' &&
    Object.values(CustomPageChatContextIdentifier).includes(
      chatContextIdentifier as CustomPageChatContextIdentifier
    )
  );
}

function mapContextItemToPageContext(item: ChatContextItem): BrandInsightsPageContext.Item {
  if (item.agent.identifier === (ChatContextType.Global as string)) {
    return {
      type: ChatContextType.Global,
    };
  }

  return {
    type: ChatContextType.Agent,
    data: item,
    metadata: {},
  };
}

function resolveOpenNewChatContext(
  contextItems: ChatContextItem[],
  payload: {
    subject: CustomPageActionSubject;
    chatContextIdentifier: CustomPageChatContextIdentifier;
  }
): BrandInsightsPageContext.Item | null {
  const selectedContextItem = contextItems.find((item) => item.identifier === payload.chatContextIdentifier);

  if (!selectedContextItem) {
    return null;
  }

  const baseContext = mapContextItemToPageContext(selectedContextItem);

  if (baseContext.type !== ChatContextType.Agent) {
    return baseContext;
  }

  if (typeof baseContext.data !== 'object' || baseContext.data === null) {
    return null;
  }

  return {
    ...baseContext,
    data: {
      ...baseContext.data,
      subject: payload.subject,
    },
  };
}

export function useCustomPageActionDispatch(): CustomPageDispatchAction {
  const dispatch = useContext(ChatStateDispatchContext);
  const chat = useContext(ActiveChatSessionContext);
  const contextItems = useContext(ChatContextItemsContext);
  const brandIdentifier = useActiveBrandIdentifier();

  return useCallback(
    (action: CustomPageAction) => {
      switch (action.type) {
        case 'update-subject': {
          if (chat.context.type !== ChatContextType.Agent || !hasSubjectPayload(action.payload)) {
            dispatch(EMPTY_BATCH_ACTION);
            return;
          }

          dispatch({
            type: 'chat-instance-subject-updated',
            payload: {
              subject: action.payload.subject,
            },
          });
          return;
        }

        case 'open-new-chat-for-subject': {
          if (!brandIdentifier || !hasOpenNewChatSubjectPayload(action.payload)) {
            dispatch(EMPTY_BATCH_ACTION);
            return;
          }

          const context = resolveOpenNewChatContext(contextItems, action.payload);
          if (!context) {
            dispatch(EMPTY_BATCH_ACTION);
            return;
          }

          dispatch({
            type: 'new-chat-started',
            payload: {
              brandIdentifier,
              context,
              pendingQuery: action.payload.pendingQuery ?? null,
            },
          });
          return;
        }

        case 'noop':
        default:
          // Stub mapping: custom page actions currently no-op in chat state.
          dispatch(EMPTY_BATCH_ACTION);
      }
    },
    [brandIdentifier, chat.context, contextItems, dispatch]
  );
}
